import React, { useState, useId } from "react";
import { Sparkles, ChevronDown, ChevronUp, RotateCcw, Check, HeartHandshake, HelpCircle, Eye, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useBridgeStore, bridgeActions, THEMES, MAX_THEMES, FREE_TEXT_MAX, REFLECTION_CONTROL, REFLECTION_AREAS, REFLECTION_ANSWERS, buildReflectionReply, type ThemeId, type ReflectionControlAnswer, type ReflectionAreaId, type ReflectionAreaAnswer } from "@/lib/dreamBridge";
import "./bridge-sections.css";
export default function WorldInsideMind() {
  const freeTextId = useId();
  const [reflectionOpen, setReflectionOpen] = useState(false);

  // Store subscriptions (returning stable references)
  const bridge = useBridgeStore(s => s.bridge);
  const reflection = useBridgeStore(s => s.reflection);
  const {
    freeText,
    themes
  } = bridge;
  const selectedThemes = themes ?? [];

  // Compute live gentle reply from the in-memory reflection answers
  const reply = buildReflectionReply(reflection);
  const hasAnyReflectionAnswer = Boolean(reflection.control) || Boolean(reflection.areas && Object.keys(reflection.areas).length > 0);
  return <section id="bridge-mind" aria-labelledby="bridge-mind-title" className="dtr-section ql-glass-strong rounded-2xl p-4 sm:p-6 md:p-10 border-primary/30 shadow-2xl space-y-8">
      {/* Step Header */}
      <div className="space-y-2 border-b border-border/60 pb-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="ql-eyebrow">Step 1 of 4</span>
          <span className="font-mono text-xs text-muted-foreground">
            Exploration Sandbox
          </span>
        </div>
        <h3 id="bridge-mind-title" className="font-serif text-xl sm:text-2xl md:text-3xl font-bold text-foreground break-words">
          The World Inside Your Mind
        </h3>
        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed max-w-3xl">
          Alternate selves and daydreamed worlds are not random escapes — they are a creative sandbox pointing to genuine values, desires, and energies you care about. Take a moment to describe or tag the essence of that inner life.
        </p>
      </div>

      {/* Part A: Optional Free-Text Sandbox */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between gap-3">
          <label htmlFor={freeTextId} className="text-sm font-semibold text-foreground font-mono flex items-center gap-2">
            <span>Describe an imagined life or world</span>
            <span className="text-xs font-normal text-muted-foreground font-sans">
              (optional)
            </span>
          </label>
          <span className="dtr-counter text-muted-foreground" aria-live="polite">
            {freeText.length} / {FREE_TEXT_MAX}
          </span>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          What makes this alternate life feel compelling, alive, or comforting? Write freely. This text is displayed back to you for personal reflection; it is never fed to an algorithm or sent anywhere.
        </p>
        <textarea id={freeTextId} rows={4} maxLength={FREE_TEXT_MAX} value={freeText} onChange={e => bridgeActions.setFreeText(e.target.value)} placeholder="e.g. A solitary workshop in a high mountain observatory, quietly building intricate instruments while having complete control over my daily hours..." className="ql-input text-base sm:text-sm min-h-[110px] resize-y" aria-describedby={`${freeTextId}-hint`} />
        <div id={`${freeTextId}-hint`} className="flex items-start gap-2 text-[11px] text-muted-foreground pt-0.5">
          <Info className="size-3.5 text-primary shrink-0 mt-0.5" />
          <span>
            Strict privacy note: this box is purely your own private notebook. It is never analysed or shared.
          </span>
        </div>
      </div>

      {/* Part B: 8 Core Themes */}
      <div className="space-y-3.5 pt-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h4 className="text-sm font-semibold text-foreground">
              What Core Themes Run Through That Imagined Life?
            </h4>
            <p className="text-xs text-muted-foreground">
              Choose up to {MAX_THEMES} qualities that make this imagined world feel meaningful.
            </p>
          </div>
          <span className="self-start sm:self-auto font-mono text-xs text-primary font-semibold px-2.5 py-0.5 rounded-full bg-primary/10 border border-primary/30 shadow-[0_0_8px_var(--glow-primary)]">
            {selectedThemes.length} / {MAX_THEMES} selected
          </span>
        </div>

        <div role="group" aria-label="Core Themes" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
          {THEMES.map(theme => {
          const isSelected = selectedThemes.includes(theme.id as ThemeId);
          const isMaxReached = selectedThemes.length >= MAX_THEMES && !isSelected;
          return <button key={theme.id} type="button" role="checkbox" aria-checked={isSelected} disabled={isMaxReached} onClick={() => bridgeActions.toggleTheme(theme.id as ThemeId)} data-active={isSelected} className={`ql-card p-4 text-left flex flex-col justify-between gap-2 min-h-[96px] cursor-pointer transition-all ${isSelected ? "border-primary bg-primary/10 ring-1 ring-primary/40 shadow-[0_0_16px_var(--glow-primary)]" : isMaxReached ? "opacity-40 cursor-not-allowed" : "hover:border-primary/50"}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="font-serif text-sm font-bold text-foreground leading-snug">
                    {theme.label}
                  </span>
                  <div aria-hidden="true" className={`size-5 rounded-full flex items-center justify-center shrink-0 border transition-colors ${isSelected ? "bg-primary text-primary-foreground border-primary" : "border-border/80 bg-background/50"}`}>
                    {isSelected && <Check className="size-3 stroke-[3]" />}
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">
                  {theme.hint}
                </p>
              </button>;
        })}
        </div>
      </div>

      {/* Part C: Collapsible Optional Reflection */}
      <div className="border border-border/80 rounded-xl bg-card/60 backdrop-blur-md overflow-hidden">
        <button type="button" onClick={() => setReflectionOpen(v => !v)} aria-expanded={reflectionOpen} className="w-full min-h-[52px] p-4 flex items-center justify-between gap-3 text-left hover:bg-card/80 transition-colors cursor-pointer min-w-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <HeartHandshake className="size-4 text-primary shrink-0" />
            <div className="min-w-0">
              <span className="text-xs sm:text-sm font-serif font-bold text-foreground block truncate">
                Optional Reflection: How Does Daydreaming Interact with Your Daily Life?
              </span>
              <span className="text-[11px] text-muted-foreground block truncate">
                Completely skippable · In-memory only · Never saved to disk or links
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 text-muted-foreground">
            <span className="font-mono text-xs hidden sm:inline">
              {reflectionOpen ? "Hide" : "Show"}
            </span>
            {reflectionOpen ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
          </div>
        </button>

        {reflectionOpen && <div className="p-4 sm:p-6 border-t border-border/60 space-y-6">
            <div className="p-3.5 rounded-lg bg-background/60 border border-border/60 text-xs text-muted-foreground leading-relaxed space-y-1.5">
              <p className="font-semibold text-foreground flex items-center gap-1.5">
                <Eye className="size-3.5 text-primary shrink-0" />
                <span>Zero-Storage Guarantee</span>
              </p>
              <p>
                These six questions are purely a personal pulse check. Answers live only in your current browser tab’s memory. They are <strong>never</strong> saved to local storage, never encoded in share links, never logged, and never included in any dossier PDF.
              </p>
            </div>

            {/* Question 1: Control */}
            <fieldset className="space-y-3">
              <legend className="text-xs sm:text-sm font-semibold text-foreground">
                1. In general, when you enter vivid daydreams or alternate lives, how does the steering feel?
              </legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                {REFLECTION_CONTROL.map(opt => {
              const isChecked = reflection.control === opt;
              return <label key={opt} data-checked={isChecked} className="dtr-radio-option capitalize">
                      <input type="radio" name="reflection-control" value={opt} checked={isChecked} onChange={() => bridgeActions.setReflectionControl(opt as ReflectionControlAnswer)} className="sr-only" />
                      <span aria-hidden="true" className={`size-3.5 rounded-full border flex items-center justify-center shrink-0 ${isChecked ? "border-primary bg-primary" : "border-border bg-transparent"}`}>
                        {isChecked && <span className="size-1.5 rounded-full bg-primary-foreground" />}
                      </span>
                      <span className="text-xs leading-snug">{opt}</span>
                    </label>;
            })}
              </div>
            </fieldset>

            {/* Questions 2-6: Areas of everyday life */}
            <div className="space-y-4 pt-2">
              <div>
                <h5 className="text-xs sm:text-sm font-semibold text-foreground">
                  2. Does spending time in imagined worlds ever get in the way of these everyday areas?
                </h5>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Select how each area feels currently. It is completely okay if none apply or if you prefer not to say.
                </p>
              </div>

              <div className="space-y-4">
                {REFLECTION_AREAS.map(area => {
              const currentAnswer = reflection.areas?.[area.id as ReflectionAreaId];
              return <fieldset key={area.id} className="p-3.5 rounded-xl border border-border/60 bg-background/40 space-y-2.5 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 min-w-0">
                        <legend className="text-xs font-bold text-foreground font-serif min-w-0 break-words">
                          {area.label}
                        </legend>
                        <span className="text-[11px] text-muted-foreground italic min-w-0 break-words">
                          {area.hint}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 min-[380px]:grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
                        {REFLECTION_ANSWERS.map(ans => {
                    const isChecked = currentAnswer === ans;
                    return <label key={ans} data-checked={isChecked} className="dtr-radio-option min-w-0 break-words">
                              <input type="radio" name={`reflection-area-${area.id}`} value={ans} checked={isChecked} onChange={() => bridgeActions.setReflectionArea(area.id as ReflectionAreaId, ans as ReflectionAreaAnswer)} className="sr-only" />
                              <span aria-hidden="true" className={`size-3.5 rounded-full border flex items-center justify-center shrink-0 ${isChecked ? "border-primary bg-primary" : "border-border bg-transparent"}`}>
                                {isChecked && <span className="size-1.5 rounded-full bg-primary-foreground" />}
                              </span>
                              <span className="text-xs capitalize min-w-0 break-words">{ans}</span>
                            </label>;
                  })}
                      </div>
                    </fieldset>;
            })}
              </div>
            </div>

            {/* Clear Reflection Answers Button */}
            {hasAnyReflectionAnswer && <div className="flex justify-end pt-1">
                <button type="button" onClick={() => bridgeActions.clearReflection()} className="min-h-[44px] px-3.5 rounded-full text-xs font-mono text-muted-foreground hover:text-foreground transition-colors cursor-pointer inline-flex items-center gap-1.5 min-w-0 break-words">
                  <RotateCcw className="size-3.5 shrink-0" />
                  <span className="min-w-0 break-words">Clear these answers</span>
                </button>
              </div>}

            {/* Polite Live Reply Region */}
            <div aria-live="polite" className={`p-4 sm:p-5 rounded-xl border transition-all text-xs leading-relaxed space-y-2.5 ${reply.tone === "support-note" ? "border-primary/60 bg-primary/10 shadow-[0_0_16px_var(--glow-primary)]" : reply.tone === "gentle-note" ? "border-chart-4/50 bg-chart-4/10" : "border-border/80 bg-background/60"}`}>
              <div className="flex items-center gap-2 font-serif font-bold text-xs uppercase tracking-wider text-primary">
                <Sparkles className="size-3.5" />
                <span>
                  {reply.tone === "support-note" ? "Warm Perspective & Support" : reply.tone === "gentle-note" ? "Everyday Rhythm Insights" : "Creative Sandbox Balance"}
                </span>
              </div>
              {reply.paragraphs.map((p, idx) => <p key={idx} className="text-foreground/90">
                  {p}
                </p>)}

              {/* Gentle link to Section 4 if support is indicated */}
              {reply.showSupportSection && <div className="pt-2 border-t border-border/40">
                  <Link to="#bridge-support" className="inline-flex items-center gap-1.5 text-primary hover:underline font-mono font-semibold min-h-[44px] min-w-0 break-words">
                    <HelpCircle className="size-4 shrink-0" />
                    <span className="min-w-0 break-words">
                      Read our calm educational notes and supportive resources in Section 4 &darr;
                    </span>
                  </Link>
                </div>}
            </div>
          </div>}
      </div>
    </section>;
}